import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import MatomoProvider from './components/MatomoProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTiktok, faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { trackEvent, trackLink } from './utils/matomoEvents';
import './App.css';

import Home from './components/Home';
import AvailablePuppies from './components/AvailablePuppies';
import OurGators from './components/OurGators';
import Contact from './components/Contact';
import HealthInfo from './components/HealthInfo';
import { dogProfiles } from './data/dogProfiles';
import { litterProfiles } from './data/litterProfiles';
import { litterNavItems } from './utils/litterNav';
import TestimonialSubmission from './components/TestimonialSubmission';
import NavDropdown from './components/NavDropdown';
import ThemeToggle from './components/ThemeToggle';

function Header() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // One entry per current litter, in the same order they appear on the page.
  const litterMenu = litterNavItems(litterProfiles.litters).map(litter => ({
    key: litter.id,
    to: litter.to,
    label: litter.label
  }));

  const gatorMenu = Object.values(dogProfiles)
    .filter(dog => dog.visible)
    .map(dog => ({ key: dog.id, to: dog.path, label: dog.name }));

  const toggleMobileMenu = () => {
    const newState = !showMobileMenu;
    setShowMobileMenu(newState);
    trackEvent('Navigation', 'ToggleMobileMenu', newState ? 'Open' : 'Close');
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  // Track navigation clicks
  const handleNavClick = (pageName) => {
    trackEvent('Navigation', 'ClickMainNav', pageName);
    closeMobileMenu();
  };

  // Track social media clicks
  const handleSocialClick = (platform, url) => {
    trackEvent('Social', 'ClickLink', platform);
    trackLink(url);
  };

  // Track phone click
  const handlePhoneClick = () => {
    trackEvent('Contact', 'ClickPhone', '337-352-2160');
  };

  return (
    <header className="header">
      <h1>
        <img src="/gsf_logo.jpg" alt="Gator Skin Frenchies Logo" className="header-logo" />
        Gator Skin Frenchies
      </h1>
      <button className="hamburger-menu" onClick={toggleMobileMenu}>
        <span></span>
        <span></span>
        <span></span>
      </button>
      <div className={`header-right ${showMobileMenu ? 'show' : ''}`}>
        <nav>
          <Link to="/" onClick={() => handleNavClick('Home')}>Home</Link>
          {/* With more than one litter listed, "Available Puppies" opens a menu
              of them. With one litter (or none) there is nothing to choose, so
              NavDropdown falls back to a plain link. */}
          <NavDropdown
            to="/puppies"
            label="Available Puppies"
            items={litterMenu.length > 1 ? litterMenu : []}
            caption="Current litters"
            footer={{ to: '/puppies', label: 'All litters' }}
            parentMenuOpen={showMobileMenu}
            onNavClick={(name, item) =>
              handleNavClick(item ? `Dropdown - ${name}` : 'Available Puppies')
            }
            onToggleSubmenu={open =>
              trackEvent('Navigation', 'ToggleSubmenu', `Available Puppies - ${open ? 'Open' : 'Close'}`)
            }
          />
          <NavDropdown
            to="/our-gators"
            label="Our Gators"
            items={gatorMenu}
            parentMenuOpen={showMobileMenu}
            onNavClick={(name, item) =>
              handleNavClick(item ? `Dropdown - ${name}` : 'Our Gators')
            }
            onToggleSubmenu={open =>
              trackEvent('Navigation', 'ToggleSubmenu', `Our Gators - ${open ? 'Open' : 'Close'}`)
            }
          />
          {/* Temporarily hiding the Contact button. To restore, uncomment the following line: */}
          {/* <Link to="/contact" onClick={() => handleNavClick('Contact')}>Contact</Link> */}
        </nav>
        <div className="social-links">
          <a href="tel:3373522160" className="phone-link" onClick={handlePhoneClick}>
            <span>(337) 352-2160</span>
          </a>
          <a href="https://www.tiktok.com/@gatorskinfrenchies" target="_blank" rel="noopener noreferrer" onClick={() => handleSocialClick('TikTok', 'https://www.tiktok.com/@gatorskinfrenchies')}>
            <FontAwesomeIcon icon={faTiktok} />
          </a>
          <a href="https://www.facebook.com/GatorSkinFrenchies" target="_blank" rel="noopener noreferrer" onClick={() => handleSocialClick('Facebook', 'https://www.facebook.com/GatorSkinFrenchies')}>
            <FontAwesomeIcon icon={faFacebook} />
          </a>
          <a href="https://www.instagram.com/gatorskinfrenchies/" target="_blank" rel="noopener noreferrer" onClick={() => handleSocialClick('Instagram', 'https://www.instagram.com/gatorskinfrenchies/')}>
            <FontAwesomeIcon icon={faInstagram} />
          </a>
          <ThemeToggle onToggle={theme => trackEvent('Navigation', 'ToggleTheme', theme)} />
        </div>
      </div>
    </header>
  );
}

export default function App() {
  return (
    <Router>
      <MatomoProvider>
        <div className="app">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/puppies" element={<AvailablePuppies />} />
              <Route path="/our-gators/*" element={<OurGators />} />
              <Route path="/health" element={<HealthInfo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/testimonial-submission" element={<TestimonialSubmission />} />
            </Routes>
          </main>
        </div>
      </MatomoProvider>
    </Router>
  );
}